import { faFilePen, faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Autocomplete, Box, Button, Chip, Dialog, Grid, TextField, Toolbar, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useStyles } from '../../hooks/useStyles';
import { api } from '../../api';
import { SimpleAlertProps } from '../../components/SimpleAlert';
import { useAuth } from '../../units/auth/AuthProvider';
import { useGlobal } from '../../units/global/GlobalProvider';
import { User } from '../../api/user';
import { regLicensePlateNumber } from '../../data/LicensePlateNumber';

export const useEditCarDialog = (props: { simpleAlert?: SimpleAlertProps; reloadCars?: () => void }) => {
    const { simpleAlert, reloadCars } = props;
    const classes = useStyles();
    const auth = useAuth();
    const [open, setOpen] = useState(false);
    const [inputCarId, setInputCarId] = useState('');
    const [editMode, setEditMode] = useState<'create' | 'update'>('create');
    const [users, setUsers] = useState<User[]>([]);
    const [originUsers, setOriginUsers] = useState<User[]>([]);
    const [inputUsers, setInputUsers] = useState<User[]>([]);
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    const onCreateCar = async () => {
        await api.car
            .create({
                carId: inputCarId,
                users: inputUsers,
            })
            .then((ret) => {
                if (ret.success) {
                    reloadCars && reloadCars();
                    setOpen(false);
                    simpleAlert?.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                } else {
                    simpleAlert?.show({ autoHideDuration: 6000, severity: 'error', message: JSON.stringify(ret) });
                }
            });
    };
    const onUpdateCar = async () => {
        await api.car
            .update({
                carId: inputCarId,
                users: inputUsers,
            })
            .then((ret) => {
                if (ret.success) {
                    reloadCars && reloadCars();
                    setOpen(false);
                    simpleAlert?.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                } else {
                    simpleAlert?.show({ autoHideDuration: 6000, severity: 'error', message: JSON.stringify(ret) });
                }
            });
    };
    const onReset = () => {
        setInputCarId('');
        setUsers([]);
        setOriginUsers([]);
        setInputUsers([]);
    };
    const onStart = async () => {
        await api.user.get({}).then((ret) => {
            if (ret.success) {
                setUsers(ret.data || []);
            } else {
                setUsers([]);
            }
        });
    };
    useEffect(() => {
        if (open) {
            onStart();
        } else {
            onReset();
        }
    }, [open]);
    useEffect(() => {
        setInputUsers(users.filter((user) => originUsers.map((originUser) => originUser.userId).includes(user.userId!)));
    }, [users, originUsers]);
    return {
        classes,
        auth,
        alert,
        open,
        setOpen,
        inputCarId,
        setInputCarId,
        editMode,
        setEditMode,
        users,
        setUsers,
        originUsers,
        setOriginUsers,
        inputUsers,
        setInputUsers,
        onOpen,
        onClose,
        onCreateCar,
        onUpdateCar,
        onReset,
    };
};

export interface EditCarDialogProps extends ReturnType<typeof useEditCarDialog> {}

export const EditCarDialog = (props: EditCarDialogProps): React.ReactElement => {
    return (
        <Dialog fullWidth={true} maxWidth={'lg'} open={props.open} onClose={props.onClose}>
            <AppBar position='sticky' className={props.classes.appBar} color='primary'>
                <Toolbar>
                    <Typography style={{ width: '100%', textAlign: 'center' }} variant='h4' component={'div'}>
                        {props.editMode == 'create' ? '新增車輛' : props.editMode == 'update' ? '更新車輛' : 'unknown edit mode'}
                    </Typography>
                    <Typography style={{ position: 'absolute', right: '0px' }} variant='h4' component={'div'}>
                        <Button color='inherit' onClick={props.onClose}>
                            <Typography variant='h5'>
                                <FontAwesomeIcon icon={faXmark} size='lg' fixedWidth />
                            </Typography>
                        </Button>
                    </Typography>
                </Toolbar>
            </AppBar>
            <Grid container flexDirection={'column'} padding={4}>
                <Grid container item flexDirection={'row'}>
                    <Grid container item flexDirection={'row'} xs={12}>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <TextField
                                disabled={props.editMode == 'update'}
                                value={props.inputCarId}
                                onChange={(e) => props.setInputCarId(e.target.value.toUpperCase())}
                                variant='outlined'
                                label={
                                    <>
                                        <span style={{ color: 'red' }}>*</span>
                                        {props.inputCarId.length > 0 && !regLicensePlateNumber.test(props.inputCarId)
                                            ? '請輸入正確格式的車牌號碼'
                                            : '車牌號碼'}
                                    </>
                                }
                                error={props.inputCarId.length > 0 && !regLicensePlateNumber.test(props.inputCarId)}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12}>
                            <Autocomplete
                                multiple
                                value={props.inputUsers}
                                onChange={(event, newValue) => props.setInputUsers(newValue)}
                                options={props.users}
                                getOptionLabel={(option) => option.userName || option.account!}
                                filterSelectedOptions
                                renderInput={(params) => <TextField {...params} label='使用者' placeholder='搜尋使用者' />}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip label={option.userName || option.account!} {...getTagProps({ index })} />
                                    ))
                                }
                                disableClearable={true}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                            {props.editMode == 'create' ? (
                                <Button
                                    disabled={
                                        ![
                                            props.inputCarId.length > 0,
                                            !(props.inputCarId.length > 0 && !regLicensePlateNumber.test(props.inputCarId)),
                                        ].every((v) => v)
                                    }
                                    className={props.classes.buttonCyan}
                                    style={{ margin: 4, marginTop: 12 }}
                                    onClick={props.onCreateCar}
                                >
                                    <FontAwesomeIcon icon={faPlus} fixedWidth />
                                    {'確定新增'}
                                </Button>
                            ) : props.editMode == 'update' ? (
                                <Button
                                    disabled={
                                        ![
                                            props.inputCarId.length > 0,
                                            !(props.inputCarId.length > 0 && !regLicensePlateNumber.test(props.inputCarId)),
                                        ].every((v) => v)
                                    }
                                    className={props.classes.buttonCyan}
                                    style={{ margin: 4, marginTop: 12 }}
                                    onClick={props.onUpdateCar}
                                >
                                    <FontAwesomeIcon icon={faFilePen} fixedWidth />
                                    {'確定更新'}
                                </Button>
                            ) : (
                                <>{'unknown edit mode'}</>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Dialog>
    );
};
